/* GAH-DROP-01 - the Golf At Home buy-box price rolls $299 -> $119 on first
   paint, arm B only. Arm A (and every visitor while the test is off) sees the
   static Liquid $119 exactly as today - this file finds no html.gd-price-drop
   class and steps out without touching a node.

   Why a new ~90 lines rather than benchmark-reveal.js's count-up: that helper
   is IIFE-private, counts UP from 0 only, its parser rejects a leading '$',
   its 2500ms is a hard constant, and it is IntersectionObserver-triggered -
   which on this page fires behind the white product-page shimmer, so nobody
   would see it run.

   Three facts about this page shape everything below:

   1. The <strong> is not ours. It carries data-gd-deal-a/b/c and the DEAL-5
      follower (assets/benchmark-deal-price.js) rewrites its textContent
      whenever it differs from the arm string - once at DOMContentLoaded and
      on bfcache pageshow today, and every 60ms through a body childList
      observer the day dealTest flips true. The same goes for the badge. So
      the tween NEVER writes text into <strong> or the badge and NEVER adds
      children to them: it paints a sibling .bb-price__roll positioned over
      the <strong> and turns the <strong>'s own ink transparent (an inline
      style change, which a childList observer does not see). The struck
      price and the badge are dimmed the same way. The visitor always ENDS on
      the follower-owned nodes, so whatever the follower decides is what stays.

   2. The numbers are read from the DOM, not hardcoded. `from` is the struck
      <s> ($299); `to` is the <strong> as it reads at roll time - after the
      head follower has run, so it is the arm's string: $119, or $149/$179 if
      DEAL-5 ever arms, or $167 on the CA twin (which the apply() market gate
      excludes anyway). Anything that does not parse as prefix+digits+suffix,
      carries a decimal, changes prefix, or does not go DOWN is left alone.

   3. Nothing the visitor can see happens before two <html> classes are gone:
      gd-drop-pending (the arm is known - layout/theme.liquid lifts it from
      apply(), ig:ready, the shimmer lifting, the Intelligems <script>
      onerror, or a 3000ms cap) and gd-page-shimmer-pending (the white
      overlay has started its 220ms fade). One MutationObserver on the <html>
      class attribute drives both steps, the header-benchmark.js pattern. If
      gd-drop-pending lifts with no arm class - the control arm, or any
      release firing before a slow Intelligems - this disconnects for good:
      an arm class landing AFTER the visitor has seen $119 must not roll a
      price they already read. theme.liquid enforces the same rule from the
      other side (GD_FLAGS.dropReleased === 'static' refuses the class), so
      the class, the exposure event and the cart attribute can only ever say
      b_drop when a roll actually played.
      The rule has a second ordering: THIS SCRIPT running late. defer scripts
      wait for parsing and every earlier stylesheet, and this template is
      long, so on a slow connection this executes after the shimmer's 3200ms
      cap has already shown $119. If the shimmer is gone at script time the
      price has been seen - bail before the first step(), or an arm that was
      known early would paint $299 over a visible $119 and roll it back down
      (the visitor watches the price jump UP by $180).

   Timing, per Paul: hold $299 for 500ms so the eye registers the original,
   then 1200ms easeOutCubic - fast fall, legible landing. Whole dollars,
   Math.round, so no frame ever reads $118; the last frame writes exactly the
   <strong>'s own string, never the computed value.

   Idempotent teardown (finish) also runs on pagehide - mobile Safari's bfcache
   restores a frozen DOM, and a half-rolled overlay must not come back with it
   - and from a 6000ms safety timer in case a shimmer class ever sticks.
   Reduced motion AND forced colors (Windows High Contrast, which repaints
   author colours - the <strong>'s transparent ink included - so the overlay
   and the $119 would print on top of each other) bail at the top; the CSS
   hides the overlay for both as well, and theme.liquid's apply() withholds
   the arm class for both so those visitors count as a_static. No
   once-per-session guard: the roll plays on every full pageload by design. */
(() => {
  'use strict';

  // benchmark-pdp-main is the only emitter today, but benchmark-pdp.js needed
  // this exact guard the day a second section started carrying its script tag
  if (window.__benchmarkPriceDropInit) return;
  window.__benchmarkPriceDropInit = true;

  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia('(forced-colors: active)').matches
  ) return;

  const HOLD = 500; // ms of $299 on screen before the fall
  const DURATION = 1200; // ms of tween
  const SHIMMER_FADE = 320; // overlay fades 220ms + is removed at 260ms (theme.liquid)
  const SAFETY = 6000; // teardown even if a gate class never lifts

  const root = document.documentElement;
  // the buy box only - .sb-price (desktop sticky bar) is offscreen at load and
  // the mobile sticky bar carries no price at all
  const price = document.querySelector('.bm-pdp .buybox .bb-price');
  if (!price) return;
  const strong = price.querySelector(':scope > strong');
  const was = price.querySelector(':scope > s');
  const badge = price.querySelector(':scope > .bm-badge');
  if (!strong || !was) return;

  // '$299' -> { prefix: '$', value: 299, grouped: false, suffix: '' }.
  // null for anything with a decimal or that does not start non-digits,
  // digits, non-digits - a CAD '$1,299.00' or a bare word is refused, not
  // guessed at.
  const parse = (text) => {
    const trimmed = (text || '').trim();
    if (trimmed.indexOf('.') !== -1) return null;
    const match = /^([^\d]*)(\d[\d,]*)(\D*)$/.exec(trimmed);
    if (!match) return null;
    return {
      prefix: match[1],
      value: Number(match[2].replace(/,/g, '')),
      grouped: match[2].indexOf(',') !== -1,
      suffix: match[3],
    };
  };

  let done = false;
  let prepared = false;
  let roll = null;
  let from = null;
  let observer = null;
  let safetyTimer = 0;

  // The one exit. Puts the follower-owned nodes back in charge: overlay gone,
  // <strong> ink restored, struck price and badge faded back in over 200ms.
  // Safe to call at any point, any number of times - before prepare() it only
  // stops the observer, so a bfcache return cannot start a roll late.
  const finish = () => {
    if (done) return;
    done = true;
    if (observer) observer.disconnect();
    window.clearTimeout(safetyTimer);
    if (!prepared) return;
    if (roll && roll.parentNode) roll.parentNode.removeChild(roll);
    roll = null;
    strong.style.color = '';
    [was, badge].forEach((node) => {
      if (!node) return;
      node.style.transition = 'opacity .2s';
      node.style.opacity = '';
      window.setTimeout(() => {
        node.style.transition = '';
      }, 250);
    });
  };

  // Runs the instant gd-drop-pending lifts with the arm class present - in
  // the same microtask as the class change, so the price never paints $119
  // first. Everything here is inline style or a SIBLING insertion; the
  // stamped <strong> and badge get no text and no children.
  const prepare = () => {
    from = parse(was.textContent);
    if (!from) {
      finish();
      return;
    }
    prepared = true;
    roll = document.createElement('span');
    roll.className = 'bb-price__roll';
    roll.setAttribute('aria-hidden', 'true');
    // .bb-price is position:relative (benchmark-pdp.css), so the <strong>'s
    // offsets are relative to it and the overlay lands on the same pixel
    roll.style.left = `${strong.offsetLeft}px`;
    roll.style.top = `${strong.offsetTop}px`;
    roll.textContent = was.textContent.trim();
    strong.insertAdjacentElement('afterend', roll);
    strong.style.color = 'transparent';
    was.style.opacity = '0';
    if (badge) badge.style.opacity = '0';
    safetyTimer = window.setTimeout(finish, SAFETY);
  };

  const run = () => {
    if (done) return;
    // read NOW, not at prepare(): the head follower runs at DOMContentLoaded,
    // after this defer script executed, and Intelligems can re-run apply()
    // any time before the shimmer lifts
    const to = parse(strong.textContent);
    if (!to || to.prefix !== from.prefix || to.suffix !== from.suffix || from.value <= to.value) {
      finish();
      return;
    }
    const grouped = from.grouped || to.grouped;
    window.setTimeout(() => {
      if (done) return;
      let t0 = null;
      const frame = (now) => {
        if (done) return;
        if (t0 === null) t0 = now;
        const p = Math.min((now - t0) / DURATION, 1);
        if (p < 1) {
          const eased = 1 - Math.pow(1 - p, 3);
          const value = Math.round(from.value + (to.value - from.value) * eased);
          roll.textContent = from.prefix + (grouped ? value.toLocaleString('en-US') : String(value)) + to.suffix;
          window.requestAnimationFrame(frame);
          return;
        }
        // the authored string, re-read this instant - never the computed value
        roll.textContent = strong.textContent.trim();
        finish();
      };
      window.requestAnimationFrame(frame);
    }, HOLD);
  };

  // Price already seen? Then there is nothing to roll and nothing to set up
  // (fact 3, second ordering). The shimmer class is added in <head> and lifts
  // no earlier than DOMContentLoaded (the GAH-QUICKWINS-01 CLS-1 path, which
  // also waits for Intelligems to settle), window load or its 3200ms cap. The
  // first two always come after this defer script has run - never re-key that
  // lift to document.readyState - so its absence here means the cap beat us
  // and $119 has been on screen. 'static' is theme.liquid's record of the
  // gate lifting before the arm was known; belt and braces, since apply()
  // then never writes the class either. QA (?gd_drop=on lifts the gate in
  // <head>) is unaffected: the shimmer class is still pending at script time.
  if (
    (window.GD_FLAGS && window.GD_FLAGS.dropReleased === 'static') ||
    (!root.classList.contains('gd-drop-pending') && !root.classList.contains('gd-page-shimmer-pending'))
  ) return;

  // Both gates, in order, re-evaluated on every <html> class change. Step (a):
  // gd-drop-pending gone -> control arm disconnects for good, arm B prepares.
  // Step (b): gd-page-shimmer-pending gone too -> the roll starts once the
  // overlay has finished fading. At script time (the first call below) at
  // least one class is still present - the guard above returned otherwise -
  // so falling through both gates here can only happen from the observer,
  // i.e. after the price was held and then released with the arm known.
  const step = () => {
    if (done) return;
    if (root.classList.contains('gd-drop-pending')) return;
    if (!prepared) {
      if (!root.classList.contains('gd-price-drop')) {
        finish();
        return;
      }
      prepare();
      if (done) return;
    }
    if (root.classList.contains('gd-page-shimmer-pending')) return;
    observer.disconnect();
    window.setTimeout(run, SHIMMER_FADE);
  };

  observer = new MutationObserver(step);
  observer.observe(root, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('pagehide', finish);
  step();
})();
